import { useState, useCallback, useEffect, useRef, type ElementType } from 'react';
import {
    Box,
    Button,
    Flex,
    Grid,
    GridItem,
    HStack,
    Icon,
    IconButton,
    Text,
} from '@chakra-ui/react';
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb';

const BRAND = '#0071E3';
const RANGE_BG = '#E8ECFE';

const startOfDay = (d: Date): Date => {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
};

const sameDay = (a: Date, b: Date): boolean => {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
};

const compareDay = (a: Date, b: Date): number => {
    return startOfDay(a).getTime() - startOfDay(b).getTime();
};

/** 6-row grid of dates for one calendar month (includes leading/trailing adjacent-month days). */
const getMonthGrid = (year: number, month: number): { date: Date; inMonth: boolean }[] => {
    const first = new Date(year, month, 1);
    const startPad = first.getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells: { date: Date; inMonth: boolean }[] = [];
    const prevMonthLast = new Date(year, month, 0).getDate();
    for (let i = startPad - 1; i >= 0; i--) {
        cells.push({ date: new Date(year, month - 1, prevMonthLast - i), inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
        cells.push({ date: new Date(year, month, d), inMonth: true });
    }
    let next = 1;
    while (cells.length < 42) {
        cells.push({ date: new Date(year, month + 1, next), inMonth: false });
        next += 1;
    }
    return cells;
};

const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export interface DualMonthRangeCalendarProps {
    startDate: Date;
    endDate: Date;
    maxDate: Date;
    /** First day of the month shown in the left calendar; updates when this value changes. */
    initialLeftMonth: Date;
    onChange?: (start: Date, end: Date) => void;
}

const DualMonthRangeCalendar = ({
    startDate,
    endDate,
    maxDate,
    initialLeftMonth,
    onChange,
}: DualMonthRangeCalendarProps) => {
    const initialLeftMs = initialLeftMonth.getTime();

    const [leftMonth, setLeftMonth] = useState(() => {
        const a = startOfDay(new Date(initialLeftMs));
        return new Date(a.getFullYear(), a.getMonth(), 1);
    });

    useEffect(() => {
        const a = startOfDay(new Date(initialLeftMs));
        setLeftMonth(new Date(a.getFullYear(), a.getMonth(), 1));
    }, [initialLeftMs]);

    const max = startOfDay(maxDate);

    const selectionPhaseRef = useRef<'start' | 'end'>('start');

    const handleDayClick = useCallback(
        (raw: Date) => {
            if (!onChange) return;
            const d = startOfDay(raw);
            if (d.getTime() > max.getTime()) return;

            if (selectionPhaseRef.current === 'start') {
                onChange(d, d);
                selectionPhaseRef.current = 'end';
            } else {
                const s = startOfDay(startDate);
                if (sameDay(d, s)) {
                    selectionPhaseRef.current = 'start';
                    return;
                }
                if (compareDay(d, s) < 0) {
                    onChange(d, s);
                } else {
                    onChange(s, d);
                }
                selectionPhaseRef.current = 'start';
            }
        },
        [startDate, max, onChange]
    );

    const navigate = (delta: number) => {
        setLeftMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1));
    };

    const rightMonth = new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1);

    const renderMonth = (anchor: Date) => {
        const y = anchor.getFullYear();
        const m = anchor.getMonth();
        const cells = getMonthGrid(y, m);

        return (
            <Box flex="1 1 0" minW={0} maxW="100%">
                <Text fontSize="sm" fontWeight="semibold" color="gray.900" textAlign="center" mb={3}>
                    {monthNames[m]} {y}
                </Text>
                <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={0} mb={1} w="100%">
                    {dayNames.map((day) => (
                        <GridItem key={`${m}-${day}`}>
                            <Text fontSize="xs" fontWeight="semibold" color="gray.500" textAlign="center" py={1}>
                                {day}
                            </Text>
                        </GridItem>
                    ))}
                </Grid>
                <Grid templateColumns="repeat(7, minmax(0, 1fr))" gap={0} w="100%">
                    {cells.map(({ date, inMonth }, index) => {
                        if (!inMonth) {
                            return (
                                <GridItem key={`${index}-empty`}>
                                    <Box h="34px" />
                                </GridItem>
                            );
                        }

                        const d0 = startOfDay(date);
                        const disabled = d0.getTime() > max.getTime();
                        const s = startOfDay(startDate);
                        const e = startOfDay(endDate);
                        const rangeLo = compareDay(s, e) <= 0 ? s : e;
                        const rangeHi = compareDay(s, e) <= 0 ? e : s;
                        const isStart = sameDay(d0, rangeLo);
                        const isEnd = sameDay(d0, rangeHi);
                        const inMiddle =
                            d0.getTime() > rangeLo.getTime() && d0.getTime() < rangeHi.getTime();

                        let bg = 'transparent';
                        let color = 'gray.800';
                        let fontWeight: 'normal' | 'semibold' = 'normal';
                        let borderRadius = 'md';
                        let _hover: Record<string, string> = {};

                        if (disabled) {
                            color = 'gray.300';
                        } else if (isStart || isEnd) {
                            bg = BRAND;
                            color = 'white';
                            fontWeight = 'semibold';
                            _hover = { bg: '#3544a0', color: 'white' };
                        } else if (inMiddle) {
                            bg = RANGE_BG;
                            color = 'gray.800';
                            borderRadius = 'none';
                            _hover = { bg: '#dce2fb' };
                        } else if (!disabled) {
                            _hover = { bg: '#E6F1FD', color: BRAND };
                        }

                        return (
                            <GridItem key={`${index}-${d0.getTime()}`}>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    w="100%"
                                    maxW="100%"
                                    h="34px"
                                    minW={0}
                                    p={0}
                                    fontSize="sm"
                                    fontWeight={fontWeight}
                                    isDisabled={disabled}
                                    bg={bg}
                                    color={color}
                                    borderRadius={borderRadius}
                                    _hover={disabled ? {} : _hover}
                                    onClick={() => handleDayClick(date)}
                                >
                                    {date.getDate()}
                                </Button>
                            </GridItem>
                        );
                    })}
                </Grid>
            </Box>
        );
    };

    return (
        <Box w="100%" maxW="100%" minW={0} overflow="hidden">
            <Flex align="center" justify="space-between" mb={3} w="100%" minW={0}>
                <IconButton
                    aria-label="Previous months"
                    icon={<Icon as={TbChevronLeft as ElementType} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(-1)}
                    color="gray.700"
                    _hover={{ bg: '#E6F1FD', color: BRAND }}
                />
                <IconButton
                    aria-label="Next months"
                    icon={<Icon as={TbChevronRight as ElementType} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(1)}
                    color="gray.700"
                    _hover={{ bg: '#E6F1FD', color: BRAND }}
                />
            </Flex>
            <HStack align="flex-start" spacing={{ base: 3, md: 6 }} w="100%" maxW="100%" minW={0}>
                {renderMonth(leftMonth)}
                {renderMonth(rightMonth)}
            </HStack>
        </Box>
    );
};

export default DualMonthRangeCalendar;
